import React, { useState, useMemo } from "react";
import {
  Card,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Button,
  InputGroup,
  Alert,
} from "react-bootstrap";
import { Link } from "react-router-dom";

const InventoryTracker = ({ data, onRefresh }) => {
  const [filters, setFilters] = useState({
    search: "",
    stockLevel: "all",
    category: "",
  });
  console.log("inventory: " + data);

  const filteredInventory = useMemo(() => {
    let items = data?.products || [];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      items = items.filter(
        (item) =>
          item.product?.productName?.toLowerCase().includes(searchTerm) ||
          item.product?.sku?.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.stockLevel !== "all") {
      items = items.filter((item) => {
        const currentStock = item.totalAvailableStock;
        const minStock = item.product?.minStock || 0;
        switch (filters.stockLevel) {
          case "low":
            return currentStock > 0 && currentStock <= minStock;
          case "out":
            return currentStock === 0;
          case "normal":
            return currentStock > minStock;
          default:
            return true;
        }
      });
    }

    return items;
  }, [data, filters]);

  const stockStats = useMemo(() => {
    const stats = {
      total: data?.products?.length || 0,
      lowStock: 0,
      outOfStock: 0,
      normal: 0,
    };

    data?.products?.forEach((item) => {
      const currentStock = item.totalAvailableStock;
      const minStock = item.product?.minStock || 0;

      if (currentStock === 0) {
        stats.outOfStock++;
      } else if (currentStock <= minStock) {
        stats.lowStock++;
      } else {
        stats.normal++;
      }
    });

    return stats;
  }, [data]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: "", stockLevel: "all", category: "" });
  };

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Inventory Tracker</h5>
          <div className="d-flex gap-2">
            <Link
              to="/inventory"
              className="btn btn-sm btn-outline-primary"
            >
              <i className="fas fa-list me-1"></i>View All Inventory
            </Link>
            <Link
              to="/purchase-requests/new"
              className="btn btn-sm btn-outline-success"
            >
              <i className="fas fa-plus me-1"></i>Purchase Request
            </Link>
            <Button variant="outline-secondary" size="sm" onClick={onRefresh}>
              <i className="fas fa-sync-alt me-1"></i>Refresh
            </Button>
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {/* Stock Statistics */}
        <Row className="mb-4">
          <Col md={3}>
            <div className="text-center p-3 bg-light rounded">
              <h4 className="mb-1">{stockStats.total}</h4>
              <small className="text-muted">Total Items</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-3 bg-success bg-opacity-10 rounded">
              <h4 className="mb-1 text-success">{stockStats.normal}</h4>
              <small className="text-muted">Normal Stock</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
              <h4 className="mb-1 text-warning">{stockStats.lowStock}</h4>
              <small className="text-muted">Low Stock</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-3 bg-danger bg-opacity-10 rounded">
              <h4 className="mb-1 text-danger">{stockStats.outOfStock}</h4>
              <small className="text-muted">Out of Stock</small>
            </div>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={4}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
              <Button variant="outline-secondary" onClick={clearFilters}>
                <i className="fas fa-times"></i>
              </Button>
            </InputGroup>
          </Col>
          <Col md={3}>
            <Form.Select
              value={filters.stockLevel}
              onChange={(e) => handleFilterChange("stockLevel", e.target.value)}
            >
              <option value="all">All Stock Levels</option>
              <option value="normal">Normal Stock</option>
              <option value="low">Low Stock</option>
              <option value="out">Out of Stock</option>
            </Form.Select>
          </Col>
        </Row>

        {/* Alerts */}
        {stockStats.outOfStock > 0 && (
          <Alert variant="danger" className="mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {stockStats.outOfStock} item(s) are out of stock!
          </Alert>
        )}
        {stockStats.lowStock > 0 && (
          <Alert variant="warning" className="mb-3">
            <i className="fas fa-exclamation-circle me-2"></i>
            {stockStats.lowStock} item(s) have low stock levels.
          </Alert>
        )}

        {/* Inventory Table */}
        <InventoryTable items={filteredInventory} />
      </Card.Body>
    </Card>
  );
};

const InventoryTable = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No inventory items found</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table hover className="mb-0">
        <thead className="table-light">
          <tr>
            <th>Product</th>
            <th>SKU</th>
            <th>Current Stock</th>
            <th>Min Stock</th>
            <th>Stock Level</th>
            <th>Last Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            function formatStock(value) {
              if (value % 1 === 0) {
                return value.toString(); // integer → no decimals
              }
              return value.toFixed(2); // float → 2 decimals
            }

            const currentStock = formatStock(item.totalAvailableStock);
            const minStock = formatStock(item.product?.minStock || 0);
            const unit = item.product.unitId.unitCode || "";
            // const stockPercentage = minStock
            //   ? Math.min((currentStock / (minStock * 2)) * 100, 100)
            //   : 100;
            const stockStatus = getStockStatus(currentStock, minStock);

            return (
              <tr key={item.product?._id || index}>
                <td>
                  <div>
                    <div className="fw-medium">{item.product?.productName}</div>
                    {item.product?.category && (
                      <small className="text-muted">
                        {item.product.category}
                      </small>
                    )}
                  </div>
                </td>
                <td>{item.product?.sku || "-"}</td>
                <td>
                  <span className={`fw-medium ${stockStatus.color}`}>
                    {currentStock} {unit}
                  </span>
                </td>
                <td>
                  {minStock} {unit}
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {/* <ProgressBar
                      now={stockPercentage}
                      variant={stockStatus.variant}
                      style={{ width: "60px", height: "8px" }}
                    /> */}
                    <Badge bg={stockStatus.variant}>{stockStatus.label}</Badge>
                  </div>
                </td>
                <td>
                  {item.stockByLocation?.[0]?.lastUpdated
                    ? new Date(
                        item.stockByLocation[0].lastUpdated
                      ).toLocaleDateString("en-IN")
                    : "-"}
                </td>
                <td>
                  <div className="d-flex gap-1">
                    {currentStock <= minStock && (
                      <Link
                        to={`/purchase-requests/new?product=${item.product?._id}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Order
                      </Link>
                    )}
                    <Link
                      to={`/inventory/${item.product?._id}`}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      View
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

const getStockStatus = (currentStock, minStock) => {
  if (currentStock === 0) {
    return { label: "Out", variant: "danger", color: "text-danger" };
  } else if (currentStock <= minStock) {
    return { label: "Low", variant: "warning", color: "text-warning" };
  } else {
    return { label: "Normal", variant: "success", color: "text-success" };
  }
};

export default InventoryTracker;
